/**
 * Hook: useCustomerContracts
 * Busca todos os contratos vinculados aos negócios de um cliente via RPC server-side
 * Substitui consultas diretas (padrão segurança por abstração)
 * 
 * @version 2.0.0
 * RELEASE: 2026-04-09 — Migração para RPC get_contracts_by_customer
 */
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { createLogger } from '@/lib/logger';

// Logger do módulo
const logger = createLogger('CustomerContracts');

/**
 * Interface do contrato com dados do deal associado
 * Estrutura flat retornada pela RPC get_contracts_by_customer
 */
export interface CustomerContract {
  id: string;
  title: string;
  status: string;
  contract_number: number | null;
  created_at: string;
  sent_at: string | null;
  signed_at: string | null;
  generated_at: string | null;
  deal_id: string;
  /** Título do negócio associado */
  deal_title: string | null;
  template_id: string | null;
  /** Nome do template */
  template_name: string | null;
  /** Categoria do template */
  template_category: string | null;
  /** Versão do template usada */
  version_number: number | null;
  /** Caminho do PDF no storage */
  pdf_storage_path: string | null;
  /** Erro da fila de processamento */
  queue_error: string | null;
  /** Signatários do contrato (JSONB array da RPC) */
  signers: Array<{
    id: string;
    signer_name: string;
    signer_email: string | null;
    signer_phone: string | null;
    signer_role: string;
    sign_order: number;
    status: string;
    signed_at: string | null;
  }>;
  /** Tracking do provider de assinatura (JSONB array da RPC) */
  provider_tracking: Array<{
    external_url: string | null;
    external_document_id: string | null;
    /** v1.2.0: nome do provedor retornado pela RPC */
    provider_name?: string | null;
    provider_slug?: string | null;
  }>;
  /** Campos mapeados para compatibilidade com ContractsTableView */
  contract_templates: {
    id: string;
    name: string;
    category: string | null;
  } | null;
  contract_signatures: Array<{
    id: string;
    signer_name: string;
    signer_email: string | null;
    status: string;
    signed_at: string | null;
  }>;
  contract_provider_tracking: Array<{
    external_url: string | null;
    external_document_id: string | null;
    provider_name?: string | null;
    provider_slug?: string | null;
  }>;
  contract_template_versions: {
    version_number: number;
  } | null;
}

/**
 * Hook para buscar contratos de todos os deals de um cliente via RPC
 * 
 * @param customerId - ID do cliente
 * @param contactId - ID do contato principal (fallback para busca)
 * @param isActive - Se a aba está ativa (lazy loading)
 */
export function useCustomerContracts(
  customerId: string | null,
  contactId: string | null | undefined,
  isActive: boolean = false,
  /** v2.1.0: filtra contratos vinculados a um veículo (contract_vehicles) */
  vehicleId: string | null = null
) {
  const { currentTenant } = useAuth();

  return useQuery({
    queryKey: ['customer-contracts', customerId, contactId, vehicleId],
    queryFn: async (): Promise<CustomerContract[]> => {
      if ((!customerId && !contactId && !vehicleId) || !currentTenant?.id) return [];

      logger.debug('Buscando contratos via RPC:', { customerId, contactId, vehicleId });

      const { data, error } = await supabase
        .rpc('get_contracts_by_customer', {
          p_customer_id: customerId,
          p_contact_id: contactId || null,
          p_vehicle_id: vehicleId,
        } as any);

      if (error) {
        logger.error('Erro ao buscar contratos do cliente:', error.message);
        throw error;
      }

      logger.info('Contratos do cliente carregados:', { count: data?.length });

      // Mapeia resultado flat da RPC para interface com campos de compatibilidade
      return (data || []).map((row: any) => ({
        ...row,
        // Campos de compatibilidade para ContractsTableView
        contract_templates: row.template_id ? {
          id: row.template_id,
          name: row.template_name,
          category: row.template_category,
        } : null,
        contract_signatures: row.signers || [],
        contract_provider_tracking: row.provider_tracking || [],
        contract_template_versions: row.version_number != null
          ? { version_number: row.version_number }
          : null,
      })) as CustomerContract[];
    },
    enabled: isActive && (!!customerId || !!contactId || !!vehicleId) && !!currentTenant?.id,
    staleTime: 0, // Dados sempre frescos ao abrir a aba
  });
}
